import {
  Box,
  Paper,
  Typography,
  useTheme,
  CircularProgress,
  Grid,
} from "@mui/material";
import dayjs from "dayjs";
import api from "../../../services/api";
import { useEffect, useRef, useState } from "react";
import useStyles from "./styles";
import MetricaApiComponent from "../../../components/metricasApi/metricaApiComponent";
import GraphMetricasApi from "../../../components/metricasApi/graphMetricasApi";

//Funcao para buscar os ultimos 90 dias e realizar analise com os dados da api
//Essa funcao serve para que seja renderizado todos os 90 dias, pois caso a API nao tenha
// dados referente ao 90 dias, o mesmo vai renderizar os dados da cor cinza
const dateGenerate = () => {
  const ultimos90Dias = [];
  for (let i = 0; i < 90; i++) {
    const data = new Date();
    data.setDate(data.getDate() - i);
    const objeto = {
      data: dayjs(
        data
          .toLocaleString("en-US", {
            timeZoneName: "longOffset",
            timeZone: "America/Sao_paulo",
          })
          .split(",")[0],
      ).format("YYYY-MM-DD"), // Formato YYYY-MM-DD
    };

    ultimos90Dias.unshift(objeto);
  }
  return ultimos90Dias;
};

export default function Status() {
  const [day, setDay] = useState(30);
  const theme = useTheme();
  const styles = useStyles(theme);
  const [nameApis, setNameApis] = useState([]);
  const [dataApis, setDataApis] = useState({});
  const [tempo, setTempo] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dataGrafico, setDataGrafico] = useState([]);
  const [dias, setDias] = useState(dateGenerate);
  const [loading, setLoading] = useState(true);
  const [graficoLoading, setGraficoLoading] = useState(true);
  const [apiConfigs, setApiConfigs] = useState([]);
  const ref = useRef(null);

  //Ao iniciar o componente, chamar a funcao getDataApi() para receber os dados da API
  useEffect(() => {
    setLoading(true);
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    getDataApi(); //Chamada da funcao para buscar dados das APIs no backend
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  //Ao receber os dados da API, fazer um filtro de apenas os dados que exista
  useEffect(() => {
    //Eetado para identificar o nome das APIs que foram identificadas no backend
    setNameApis(Object.keys(dataApis));
  }, [dataApis]);

  //Hook para realizar a renderização dos dados em diferentes telas diferentes
  //Aqui se encontra as funcionalidades de alterar a quantidade de dias que esta
  // sendo monitorado de acordo com o tamanho da tela
  useEffect(() => {
    //setar tempo multiplos de 30 de acordo com o tamanho da tela

    const a = -520.3654371226432;
    const b = 86.34673864454844;
    let func = ((x) => ((a + b * Math.log(x)) / 10).toFixed(0)) || 0;

    setTempo(
      parseInt(
        10 * func(windowWidth) <= 30
          ? 30
          : 10 * func(windowWidth) >= 90
            ? 90
            : 10 * func(windowWidth),
      ),
    );
  }, [windowWidth]);

  //Hook para buscar os dados do gráfico de tempo de resposta sempre que o período
  //selecionado mudar. A agregação (5min/hora/dia) é feita no backend de acordo com "day"
  useEffect(() => {
    getDataGrafico(day || 30);
  }, [day]);

  //Funcao para buscar dados da API (barras de atividade + configuração de cor das APIs)
  const getDataApi = async () => {
    try {
      const response = await api.get("/interno/metricas");
      setDataApis(response?.data?.dataAPis || {});
      const configColorsApi = response?.data?.apiConfigs;

      const colorKeys = Object.keys(theme.palette.colors)?.filter(
        (color) => color !== "white" && color !== "black",
      );

      if (configColorsApi) {
        const configColors = configColorsApi?.map((apiConf, index) => {
          // escolhe a cor em ordem, repetindo do começo se passar do limite
          const colorName = colorKeys[index % colorKeys?.length];
          const colorValue = theme.palette.colors[colorName].medium;

          return {
            ...apiConf,
            color: colorValue,
            [apiConf?.label?.toLowerCase()]: colorValue,
          };
        });
        setApiConfigs(configColors);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  //Funcao para buscar os dados do gráfico de tempo de resposta para o período selecionado.
  //O backend já retorna os pontos agregados (5min/hora/dia), sem precisar filtrar no cliente
  const getDataGrafico = async (dias) => {
    try {
      setGraficoLoading(true);
      const response = await api.post("/interno/metricas/grafico", {
        dias: dias,
      });
      setDataGrafico(response?.data?.timeRequestData || []);
    } catch (error) {
      console.error(error);
    } finally {
      setGraficoLoading(false);
    }
  };

  //Realizar filtro de tempo para grafico de tempo de respostas das APIs
  const handleChange = (event) => {
    setDay(event.target.value);
  };

  //Funcao para analisar a atividade de um dia em especifico, a partir do resumo
  //já agregado pelo backend ({ total, success })
  const analiseAtividadeApiDay = (resumoDia) => {
    if (!resumoDia?.total) return 0;
    return parseFloat((resumoDia.success * 100) / resumoDia.total).toFixed(2);
  };

  //Funcao para analisar a atividade de todos os dias visiveis, a partir dos
  //resumos diarios (apenas os dias com pelo menos uma checagem)
  const analiseAtividadeApi = (resumosDiarios) => {
    if (!Array.isArray(resumosDiarios) || resumosDiarios.length === 0) {
      return 0;
    }
    const somaPercentuais = resumosDiarios.reduce(
      (acc, resumo) => acc + parseFloat(analiseAtividadeApiDay(resumo)),
      0,
    );
    const media = somaPercentuais / resumosDiarios.length;
    return isNaN(media) ? 0 : media.toFixed(2);
  };

  return (
    <Box sx={styles.containerComponent}>
      {loading ? (
        <Box sx={styles.containerLoading}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={styles.containerPaper} ref={ref}>
            <Grid
              container
              columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
              flexGrow={1}
              spacing={1}
            >
              {nameApis.map((apiName, index) => (
                <Grid
                  item
                  key={apiName}
                  xs={2}
                  sm={4}
                  md={6}
                  lg={6}
                  xl={6}
                  xxl={3}
                >
                  <Paper
                    sx={{
                      ...styles.paper,
                      backgroundColor: theme.palette.background.paper,
                    }}
                    elevation={theme.palette.mode == "dark" ? 0 : 3}
                  >
                    <MetricaApiComponent
                      apiName={apiName}
                      tempo={tempo}
                      index={index}
                      resumoDiario={dataApis?.[apiName]}
                      dias={dias}
                      analiseAtividadeApiDay={analiseAtividadeApiDay}
                      analiseAtividadeApi={analiseAtividadeApi}
                      windowWidth={windowWidth}
                      apiConfigs={apiConfigs}
                    />
                  </Paper>
                </Grid>
              ))}

              <Grid item xs={2} sm={4} md={6} lg={6} xl={6} xxl={6}>
                <Paper
                  sx={{
                    ...styles.paperGraph,
                    maxWidth: "100%",
                  }}
                  elevation={theme.palette.mode == "dark" ? 0 : 3}
                >
                  <GraphMetricasApi
                    dataGrafico={dataGrafico}
                    windowWidth={windowWidth}
                    handleChange={handleChange}
                    day={day}
                    graficoLoading={graficoLoading}
                    apiConfigs={apiConfigs}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}
